import { getExpertCompanyCompetitors } from "@/frontend/features/Company/model/getExpertCompanyCompetitors";
import { getExpertCompanyMarketOverview } from "@/frontend/features/Company/model/getExpertCompanyMarketOverview";
import { getExpertCompanyPatents } from "@/frontend/features/Company/model/getExpertCompanyPatents";
import { getExpertCompanyProducts } from "@/frontend/features/Company/model/getExpertCompanyProducts";
import { getExpertCompanyProfile } from "@/frontend/features/Company/model/getExpertCompanyProfile";
import { getExpertCompanySimilarCompanies } from "@/frontend/features/Company/model/getExpertCompanySimilarCompanies";
import { getExpertCompanyTeam } from "@/frontend/features/Company/model/getExpertCompanyTeam";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { TParsingStatus } from "@/libs/models/portfolio-companies";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

export function getExpertCompanyData(organizationId: number, companyId: number) {
  // company details
  const [detailsDataStatus, setDetailsDataStatus] = useState<TParsingStatus>("processing");
  const {
    data: detailsData,
    isLoading: isLoadingDetailsData,
    isFetching: isFetchingDetailsData,
    refetch: refetchDetailsData,
    error: detailsDataError
  } = useQuery({
    queryKey: ["expertCompanyDetails", companyId, organizationId],
    queryFn: async () => {
      const response = await getExpertCompanyProfile({ organizationId, portfolioCompanyId: companyId });
      setDetailsDataStatus(response.parsingStatus);
      return response;
    },
    refetchInterval: 5000,
    enabled: detailsDataStatus !== "done",
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (detailsDataError) {
      console.error(detailsDataError);
      setDetailsDataStatus("done");
      reportErrorToSentry(
        detailsDataError,
        "EXPERT PORTFOLIO COMPANY PROFILE: /experts/organizations/${orgId}/portfolio-companies/${id}/company-details"
      );
      snackbarConstructor("Error fetching company details", "error");
    }
  }, [detailsDataError]);

  // similar companies
  const [similarCompaniesDataStatus, setSimilarCompaniesDataStatus] = useState<TParsingStatus | null>("processing");
  const {
    data: similarCompaniesData,
    isLoading: isLoadingSimilarCompaniesData,
    refetch: refetchSimilarCompaniesData,
    error: similarCompaniesDataError
  } = useQuery({
    queryKey: ["expertCompanySimilarCompanies", companyId, organizationId],
    queryFn: async () => {
      const response = await getExpertCompanySimilarCompanies({ organizationId, portfolioCompanyId: companyId });
      setSimilarCompaniesDataStatus(response.status);

      return response;
    },
    refetchInterval: 5000,
    enabled: similarCompaniesDataStatus !== "done",
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (similarCompaniesDataError) {
      console.error(similarCompaniesDataError);
      snackbarConstructor("Error fetching similar companies", "error");
      setSimilarCompaniesDataStatus("done");
      reportErrorToSentry(
        similarCompaniesDataError,
        "EXPERT PORTFOLIO COMPANY PROFILE: /experts/organizations/${orgId}/portfolio-companies/${id}/similar-companies"
      );
    }
  }, [similarCompaniesDataError]);

  // competitors

  const [competitorsDataStatus, setCompetitorsDataStatus] = useState<TParsingStatus | null>("processing");
  const {
    data: competitorCompaniesData,
    isLoading: isLoadingCompetitorCompaniesData,
    refetch: refetchCompetitorCompaniesData,

    error: competitorsDataError
  } = useQuery({
    queryKey: ["expertCompanyCompetitorCompanies", companyId, organizationId],
    queryFn: async () => {
      const response = await getExpertCompanyCompetitors({ organizationId, portfolioCompanyId: companyId });
      setCompetitorsDataStatus(response.status);

      return response.data;
    },
    refetchInterval: 5000,
    enabled: competitorsDataStatus !== "done",
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (competitorsDataError) {
      console.error(competitorsDataError);
      snackbarConstructor("Error fetching competitors", "error");
      setCompetitorsDataStatus("done");
      reportErrorToSentry(
        competitorsDataError,
        "EXPERT PORTFOLIO COMPANY PROFILE: /experts/organizations/${orgId}/portfolio-companies/${id}/competitor-companies"
      );
    }
  }, [competitorsDataError]);

  // market overview

  const [marketOverviewDataStatus, setMarketOverviewDataStatus] = useState<TParsingStatus | null>("processing");

  const {
    data: marketOverviewData,
    isLoading: isLoadingMarketOverviewData,
    refetch: refetchMarketOverviewData,
    error: marketOverviewDataError
  } = useQuery({
    queryKey: ["expertCompanyMarketOverview", companyId, organizationId],
    queryFn: async () => {
      const response = await getExpertCompanyMarketOverview({ organizationId, portfolioCompanyId: companyId });
      setMarketOverviewDataStatus(response.status);
      return response;
    },
    refetchInterval: 5000,
    enabled: marketOverviewDataStatus !== "done",
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (marketOverviewDataError) {
      console.error(marketOverviewDataError);
      snackbarConstructor("Error fetching market overview", "error");
      setMarketOverviewDataStatus("done");
      reportErrorToSentry(
        marketOverviewDataError,
        "EXPERT PORTFOLIO COMPANY PROFILE: /experts/organizations/${orgId}/portfolio-companies/${id}/market-report"
      );
    }
  }, [marketOverviewDataError]);

  // patents

  const {
    data: patentsData,
    isLoading: isLoadingPatentsData,
    refetch: refetchPatentsData,
    error: patentsDataError
  } = useQuery({
    queryKey: ["expertCompanyPatents", companyId, organizationId],
    queryFn: async () => {
      const response = await getExpertCompanyPatents({ organizationId, portfolioCompanyId: companyId });
      return response;
    },
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (patentsDataError) {
      console.error(patentsDataError);
      snackbarConstructor("Error fetching patents", "error");
      reportErrorToSentry(
        patentsDataError,
        "EXPERT PORTFOLIO COMPANY PROFILE: /experts/organizations/${orgId}/portfolio-companies/${id}/patents"
      );
    }
  }, [patentsDataError]);

  // products
  const [productsDataStatus, setProductsDataStatus] = useState<TParsingStatus | null>("processing");

  const {
    data: productsData,
    isLoading: isLoadingProductsData,
    refetch: refetchProductsData,
    error: productsDataError
  } = useQuery({
    queryKey: ["expertCompanyProducts", companyId, organizationId],
    queryFn: async () => {
      const response = await getExpertCompanyProducts({ organizationId, portfolioCompanyId: companyId });
      setProductsDataStatus(response.status);
      return response;
    },
    refetchInterval: 5000,
    enabled: productsDataStatus !== "done",
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (productsDataError) {
      console.error(productsDataError);
      snackbarConstructor("Error fetching products", "error");
      setProductsDataStatus("done");
      reportErrorToSentry(
        productsDataError,
        "EXPERT PORTFOLIO COMPANY PROFILE: /experts/organizations/${orgId}/portfolio-companies/${id}/products"
      );
    }
  }, [productsDataError]);

  // team by Innovera

  const {
    data: teamData,
    isLoading: isLoadingTeamData,
    refetch: refetchTeamData,
    error: teamDataError
  } = useQuery({
    queryKey: ["expertCompanyTeam", companyId, organizationId],
    queryFn: () => getExpertCompanyTeam({ organizationId, portfolioCompanyId: companyId }),
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (teamDataError) {
      console.error(teamDataError);
      snackbarConstructor("Error fetching team", "error");
      reportErrorToSentry(
        teamDataError,
        "EXPERT PORTFOLIO COMPANY PROFILE: /experts/organizations/${orgId}/portfolio-companies/${id}/team"
      );
    }
  }, [teamDataError]);

  return {
    detailsData,
    isLoadingDetailsData,
    isFetchingDetailsData,
    detailsDataStatus,
    refetchDetailsData,
    competitorCompaniesData,
    isLoadingCompetitorCompaniesData,
    refetchCompetitorCompaniesData,
    competitorsDataStatus,
    similarCompaniesData,
    isLoadingSimilarCompaniesData,
    refetchSimilarCompaniesData,
    similarCompaniesDataStatus,
    marketOverviewData,
    isLoadingMarketOverviewData,
    refetchMarketOverviewData,
    marketOverviewDataStatus,
    patentsData,
    isLoadingPatentsData,
    refetchPatentsData,
    productsData,
    isLoadingProductsData,
    refetchProductsData,
    productsDataStatus,
    teamData,
    isLoadingTeamData,
    refetchTeamData
  };
}
